/**
 * Guest (free-to-play) identity — a localStorage session UUID carried through the
 * app as an address-shaped sentinel string so it flows through every existing
 * walletAddress/owner parameter. See docs/superpowers/specs/free-to-play-tier.md.
 *
 * NOT a wallet: it must never reach any chain call or linked_wallets.
 */
export const GUEST_WALLET_PREFIX = 'guest:';

/** App-generated pet_id prefix for free pets (no on-chain object). */
export const FREE_PET_ID_PREFIX = 'free_';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function guestWalletForSession(sessionId: string): string {
  return `${GUEST_WALLET_PREFIX}${sessionId}`;
}

export function sessionFromGuestWallet(address: string | null | undefined): string | null {
  if (!address || !address.startsWith(GUEST_WALLET_PREFIX)) return null;
  const sessionId = address.slice(GUEST_WALLET_PREFIX.length);
  return UUID_RE.test(sessionId) ? sessionId : null;
}

export function isGuestWallet(address: string | null | undefined): boolean {
  return sessionFromGuestWallet(address) !== null;
}

/** True for an app-generated free-pet id, e.g. `free_3fa85f64-5717-4562-b3fc-2c963f66afa6`. */
export function isFreePetId(petId: string | null | undefined): boolean {
  if (!petId || !petId.startsWith(FREE_PET_ID_PREFIX)) return false;
  return UUID_RE.test(petId.slice(FREE_PET_ID_PREFIX.length));
}

/**
 * True when an address is NOT a real connected wallet — either absent (null/empty) or
 * the guest sentinel. Convenience for UI that only has an address to reason about
 * (wallet cards, transaction lists) so nobody re-hardcodes `startsWith('guest:')`.
 *
 * App-wide connection STATE (is the player connected? which address acts for them?)
 * must still come from `useWalletConnection()` — this is only for "is THIS address a
 * real wallet?" display decisions.
 */
export function isGuestOrNoWallet(address: string | null | undefined): boolean {
  return !address || isGuestWallet(address);
}

/**
 * Minimal pet shape for identity questions — the three ownership keys. Mirrors the
 * columns set at free-pet creation (backend freeAdoptionService) and the triple-key
 * rule in petAuthorization.assertAccountOwnsPet.
 */
export interface PetOwnershipKeys {
  /** Internal account id — a JWT login on ANY chain (Sui / EVM / Solana / email). */
  account_id?: string | null;
  /** A connected wallet address on ANY chain (recorded even before a JWT exists). */
  owner_address?: string | null;
  /** Guest (free-tier) session key — set when the pet has no real owner yet. */
  pvp_session_id?: string | null;
}

/**
 * THE definition of "signed up / connected" for a pet: it is tied to a real owner
 * identity. CHAIN-AGNOSTIC by design — an `account_id` (JWT login on any chain) OR an
 * `owner_address` (a connected wallet, any chain) both count. Use this instead of
 * hand-rolling the check.
 *
 * ⚠️  DO NOT gate on `account_id` alone — that drops wallet-connected players who have
 * no JWT yet (common for EVM/Solana), and DO NOT gate on a Sui address specifically.
 * Both mistakes have wrongly capped free-tier players; that's why this lives in one place.
 */
export function petHasOwnerIdentity(pet: PetOwnershipKeys): boolean {
  return Boolean(pet.account_id) || Boolean(pet.owner_address);
}

/**
 * True when a pet has NO owner identity — an anonymous guest, keyed only by its play
 * session (`pvp_session_id`). The exact inverse of {@link petHasOwnerIdentity}. This is
 * who the free-tier sign-up gates (level cap, campaign boundary) are for.
 */
export function isAnonymousGuestPet(pet: PetOwnershipKeys): boolean {
  return !petHasOwnerIdentity(pet);
}
